import { useState } from 'react';
import { getCatalogProducts, getFeaturedProducts, matchesProductQuery } from './components/Product/productData';
import ProductCatalog from './components/ProductCatalog/ProductCatalog';
import ProductSearch from './components/ProductSearch/ProductSearch';
import constants from './constants/Constants';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import useProductFilters from './hooks/useProductFilters';

const FILTERS = [
  { label: 'All', value: null },
  { label: 'AI', value: constants.TABS.AI },
  { label: 'Spotify', value: constants.TABS.SPOTIFY },
  { label: 'Utilities', value: constants.TABS.UTILITIES },
];

function App() {
  const { activeTabs, setActiveTabs } = useProductFilters();
  const [searchQuery, setSearchQuery] = useState('');
  const products = getCatalogProducts();
  const query = searchQuery.trim();

  const filteredProducts = products.filter(
    (product) => (activeTabs.length === 0 || activeTabs.includes(product.category)) && matchesProductQuery(product, query)
  );
  const organized = activeTabs.length === 0 && !query;

  const toggleFilter = (value: string | null) => {
    if (!value) {
      setActiveTabs([]);
    } else if (activeTabs.includes(value)) {
      setActiveTabs(activeTabs.filter((tab) => tab !== value));
    } else {
      setActiveTabs([...activeTabs, value]);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setActiveTabs([]);
  };

  return (
    <div className="mx-auto w-full max-w-screen-xl flex-1 px-4 pb-16 pt-6 sm:px-6 lg:pt-9">
      <header className="mb-5 lg:mb-7">
        <h1 className="font-display text-balance text-[clamp(1.875rem,5vw,2.75rem)] font-extrabold tracking-tight text-foreground">
          Hi there!
        </h1>
        <p className="mt-1.5 max-w-[60ch] text-pretty text-base text-muted-foreground sm:text-lg lg:text-xl">
          I&apos;m Saarim, and I love building amazing things.{' '}
          <a href="https://saarim.me" target="_blank" rel="noopener noreferrer" className="whitespace-nowrap font-medium text-primary underline-offset-4 hover:underline">
            About me
          </a>
        </p>
      </header>

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
        <div className="w-full lg:max-w-md">
          <ProductSearch value={searchQuery} onChange={setSearchQuery} placeholder={`Search ${products.length} tools`} />
        </div>
        <div role="group" aria-label="Filter tools by category" className="flex rounded-xl bg-white/[0.06] p-[3px] lg:w-[400px]">
          {FILTERS.map((filter) => {
            const selected = filter.value ? activeTabs.includes(filter.value) : activeTabs.length === 0;
            return (
              <button
                key={filter.label}
                type="button"
                aria-pressed={selected}
                onClick={() => toggleFilter(filter.value)}
                className={cn(
                  'h-[38px] min-w-0 flex-1 rounded-[9px] px-2 text-sm font-medium transition-colors',
                  selected ? 'bg-white/20 text-white' : 'text-white/80 hover:text-white'
                )}
              >
                {filter.label}
              </button>
            );
          })}
        </div>
      </div>

      {filteredProducts.length > 0 ? (
        <ProductCatalog products={filteredProducts} featured={organized ? getFeaturedProducts(products) : []} />
      ) : (
        <div className="flex flex-col items-start gap-3 py-10">
          <h2 className="text-balance text-xl font-semibold text-foreground">
            {query ? `No tool matches "${query}"` : 'No tool matches these filters'}
          </h2>
          <p className="text-pretty text-muted-foreground">Check the spelling, or show all {products.length} tools.</p>
          <Button variant="secondary" onClick={clearSearch}>Show All Tools</Button>
        </div>
      )}
    </div>
  );
}

export default App;
